/** Minimal toast stack. Errors surface here — never a white screen. */
import { useCallback, useState } from "react";

export type ToastKind = "info" | "error" | "success";

export interface ToastItem {
  id: number;
  kind: ToastKind;
  message: string;
  /** Optional action button. */
  action?: { label: string; run: () => void };
  /** Auto-dismiss after ms (omit for sticky). */
  timeout?: number;
}

let nextId = 1;

export function useToasts() {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts((cur) => cur.filter((t) => t.id !== id));
  }, []);

  const show = useCallback(
    (t: Omit<ToastItem, "id">) => {
      const id = nextId++;
      setToasts((cur) => [...cur, { ...t, id }]);
      if (t.timeout) setTimeout(() => dismiss(id), t.timeout);
      return id;
    },
    [dismiss],
  );

  return { toasts, show, dismiss };
}

export function ToastStack({
  toasts,
  onDismiss,
}: {
  toasts: ToastItem[];
  onDismiss: (id: number) => void;
}) {
  return (
    <div className="sp-toast-stack" role="status" aria-live="polite">
      {toasts.map((t) => (
        <div key={t.id} className={`sp-toast sp-toast-${t.kind}`}>
          <span className="sp-toast-msg">{t.message}</span>
          {t.action && (
            <button
              className="sp-toast-action"
              onClick={() => {
                t.action!.run();
                onDismiss(t.id);
              }}
            >
              {t.action.label}
            </button>
          )}
          <button className="sp-toast-close" onClick={() => onDismiss(t.id)} aria-label="Dismiss">
            ×
          </button>
        </div>
      ))}
    </div>
  );
}
